import { Button, Checkbox } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  catalogApi,
  type EpcFitmentProductListItem,
  type EpcFitmentSkuListItem,
} from "../api/client";

const PRODUCT_PAGE_SIZE = 20;

const CONDITION_LABEL: Record<string, string> = {
  new: "ໃໝ່",
  used: "ໃຊ້ແລ້ວ",
  refurb: "ປັບປຸງແລ້ວ",
};

type EpcCalloutProductSkuPickerProps = {
  configurationId: string;
  productId: string;
  onProductIdChange: (id: string) => void;
  selectedSkuIds: string[];
  onSelectedSkuIdsChange: (ids: string[]) => void;
  lockProduct?: boolean;
};

function productLookupQueryFn(configurationId: string) {
  return (params: { search: string; pageParam: number }) => {
    const { search, pageParam } = params;
    return catalogApi
      .listEpcFitmentProducts(configurationId, {
        limit: PRODUCT_PAGE_SIZE,
        offset: (pageParam - 1) * PRODUCT_PAGE_SIZE,
        search: search.trim() || undefined,
      })
      .then((res) => ({
        items: res.data,
        nextPage:
          res.meta.offset + res.data.length < res.meta.total
            ? pageParam + 1
            : null,
      }));
  };
}

async function productPreloadQueryFn(
  configurationId: string,
  id: string,
): Promise<EpcFitmentProductListItem | null> {
  if (!id) return null;
  const res = await catalogApi.listEpcFitmentProducts(configurationId, {
    limit: 1,
    offset: 0,
    search: undefined,
  });
  const found = res.data.find((p) => p.id === id);
  if (found) return found as EpcFitmentProductListItem;
  try {
    const p = await catalogApi.getProduct(id);
    const fallback: EpcFitmentProductListItem = {
      id: p.id,
      name: p.name,
      type: p.type,
      status: p.status,
      categoryId: p.categoryId,
      brandId: p.brandId,
      categoryName: p.categoryName,
      brandName: p.brandName,
      thumbnailUrl: null,
      updatedAt: p.updatedAt,
      skuCount: 0,
    };
    return fallback;
  } catch {
    return null;
  }
}

function getProductLabel(item: EpcFitmentProductListItem) {
  const meta = [item.brandName, `${item.skuCount} SKU`].filter(Boolean).join(" · ");
  return meta ? `${item.name} · ${meta}` : item.name;
}

function SkuCheckboxRow({
  sku,
  checked,
  onToggle,
}: {
  sku: EpcFitmentSkuListItem;
  checked: boolean;
  onToggle: () => void;
}) {
  const label = sku.oemPartNo?.trim() || sku.id.slice(0, 8);
  const condition = sku.condition
    ? (CONDITION_LABEL[sku.condition] ?? sku.condition)
    : null;

  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:bg-muted/40 ${
        checked
          ? "border-primary/40 bg-primary/5"
          : "bg-muted/20"
      }`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={() => onToggle()}
        className="mt-0.5"
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono font-medium text-sm">{label}</span>
          {condition ? (
            <span className="rounded-md bg-background px-1.5 py-0.5 text-muted-foreground text-xs">
              {condition}
            </span>
          ) : null}
        </span>
        {sku.fitmentNote ? (
          <span className="mt-0.5 block text-muted-foreground text-xs">
            {sku.fitmentNote}
          </span>
        ) : null}
        {sku.quantityPerVehicle != null ? (
          <span className="mt-0.5 block text-muted-foreground text-xs">
            ຈຳນວນ/ຄັນ: {sku.quantityPerVehicle}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export function EpcCalloutProductSkuPicker({
  configurationId,
  productId,
  onProductIdChange,
  selectedSkuIds,
  onSelectedSkuIdsChange,
  lockProduct = false,
}: EpcCalloutProductSkuPickerProps) {
  const lookupFn = useMemo(
    () => productLookupQueryFn(configurationId),
    [configurationId],
  );

  const { data: skus, isLoading: skusLoading } = useQuery({
    queryKey: ["catalog", "epc-fitment-skus", configurationId, productId],
    queryFn: () => catalogApi.listEpcFitmentSkus(configurationId, productId),
    enabled: Boolean(configurationId && productId),
  });

  useEffect(() => {
    if (!productId) {
      if (selectedSkuIds.length > 0) onSelectedSkuIdsChange([]);
      return;
    }
    // ລໍ skus ໂຫຼດກ່ອນ — ຢ່າ clear ກ່ອນຂໍ້ມູນມາ
    if (skus === undefined) return;
    if (skus.length === 0) {
      if (selectedSkuIds.length > 0) onSelectedSkuIdsChange([]);
      return;
    }
    const valid = selectedSkuIds.filter((id) => skus.some((s) => s.id === id));
    if (valid.length !== selectedSkuIds.length) {
      onSelectedSkuIdsChange(valid);
    }
  }, [productId, skus, selectedSkuIds, onSelectedSkuIdsChange]);

  const toggleSku = (skuId: string) => {
    onSelectedSkuIdsChange(
      selectedSkuIds.includes(skuId)
        ? selectedSkuIds.filter((id) => id !== skuId)
        : [...selectedSkuIds, skuId],
    );
  };

  const selectAll = () => {
    onSelectedSkuIdsChange(skus?.map((s) => s.id) ?? []);
  };

  const clearAll = () => {
    onSelectedSkuIdsChange([]);
  };

  return (
    <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
      <div>
        <p className="mb-1.5 font-medium text-sm">1. ເລືອກສິນຄ້າ</p>
        <p className="mb-2 text-muted-foreground text-xs">
          ສະແດງເຉພາະສິນຄ້າທີ່ມີ SKU ຜູກກັບສະເປັກລົດນີ້
        </p>
        {lockProduct && productId ? (
          <LockedProductPreview
            configurationId={configurationId}
            productId={productId}
          />
        ) : (
          <InfiniteCombobox<EpcFitmentProductListItem>
            queryKey={["catalog", "epc-fitment-products", configurationId]}
            queryFn={lookupFn}
            preloadQueryFn={(id) => productPreloadQueryFn(configurationId, id)}
            getLabel={getProductLabel}
            getValue={(item) => item.id}
            value={productId}
            onValueChange={(id) => {
              onProductIdChange(id);
              onSelectedSkuIdsChange([]);
            }}
            placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ..."
            clearable
          />
        )}
      </div>

      {productId ? (
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium text-sm">2. ເລືອກ SKU / OEM</p>
              <p className="text-muted-foreground text-xs">
                ເລືອກໄດ້ຫຼາຍ SKU · ກັງຕາມການຜູກກັບສະເປັກລົດ
              </p>
            </div>
            {skus && skus.length > 0 ? (
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="outline" onClick={selectAll}>
                  ເລືອກທັງໝົດ
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={clearAll}>
                  ລ້າງ
                </Button>
              </div>
            ) : null}
          </div>

          {skusLoading ? (
            <p className="py-4 text-center text-muted-foreground text-sm">
              ກຳລັງໂຫຼດ SKU…
            </p>
          ) : !skus?.length ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-muted-foreground text-sm">
              ບໍ່ມີ SKU ທີ່ຜູກກັບສະເປັກລົດນີ້
            </p>
          ) : (
            <div className="max-h-52 space-y-1.5 overflow-y-auto pr-0.5">
              {skus.map((sku) => (
                <SkuCheckboxRow
                  key={sku.id}
                  sku={sku}
                  checked={selectedSkuIds.includes(sku.id)}
                  onToggle={() => toggleSku(sku.id)}
                />
              ))}
            </div>
          )}

          <p className="mt-2 text-muted-foreground text-xs">
            ເລືອກແລ້ວ {selectedSkuIds.length} / {skus?.length ?? 0} SKU
          </p>
        </div>
      ) : null}
    </div>
  );
}

function LockedProductPreview({
  configurationId,
  productId,
}: {
  configurationId: string;
  productId: string;
}) {
  const { data: product } = useQuery({
    queryKey: ["catalog", "epc-fitment-product", configurationId, productId],
    queryFn: () => productPreloadQueryFn(configurationId, productId),
    enabled: Boolean(productId),
  });

  if (!product) {
    return (
      <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs">
        {productId}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-md border bg-muted/20 p-2.5">
      {product.thumbnailUrl?.trim() ? (
        <AppImage
          src={resolveImageSrc(product.thumbnailUrl)}
          alt={product.name}
          className="size-14 shrink-0 rounded-md border object-cover"
          fit="cover"
        />
      ) : (
        <div className="flex size-14 shrink-0 items-center justify-center rounded-md border border-dashed bg-muted/30 text-muted-foreground text-xs">
          —
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{product.name}</p>
        <p className="text-muted-foreground text-xs">
          {[product.brandName, product.categoryName].filter(Boolean).join(" · ") ||
            "—"}
        </p>
        <p className="mt-1 text-muted-foreground text-[11px]">
          ບໍ່ປ່ຽນສິນຄ້າຕອນແກ້ໄຂ — ປັບ SKU ໄດ້
        </p>
      </div>
    </div>
  );
}
